/*
 * Proprietary and Confidential
 *
 * Copyright (c) [2018] -  [] Marcelo H. Krebber - Munich, London 2018
 * All Rights Reserved.
 *
 * Dissemination or reproduction of this file [slideout-navigation.js] or parts within
 * via any medium is strictly forbidden unless prior written permission is obtained
 * from <marcelo.krebber@gmail.com>
 *
 * Last modified: 16.10.19, 20:28
 */

import React, {Fragment, useContext} from 'react';
import {Link} from "react-router-dom";
import {useDispatch, useSelector} from 'react-redux';

import {logoutRequest} from "../../actions";
import {SlideoutContext} from "./slideout-provider";
import {
    getImprintPageUrl,
    getPrivacyPolicyPageUrl,
    getTermsOfUsePageUrl,
    getUserAvatar
} from "../../actions/environment";
import {isRegistration as getIsRegistration, useAuthorization, useSiteConfiguration} from "../../selectors";
import {useConfiguration} from "../configuration/configuration";
import {NavigationGroup, NavigationRow} from "../navigation/layout/nav-layout";
import {RoundedButton, TooltipLink} from "../navigation/buttons/nav-buttons";
import {
    AccountCircleIcon,
    AppsIcon,
    CelebrationIcon,
    ChatIcon,
    CloseIcon,
    EventIcon, fs_regular,
    HomeIcon,
    PersonAddIcon,
    PersonEditIcon,
    PowerSettingsIcon,
    SettingsIcon,
    TodayIcon
} from "../navigation/icons/nav-icons";
import NavigationLanguageDropdown, {localeFlags} from "../navigation/navigation-language-dropdown";

const HomeLink = ({to, title, close}) => {
    return <NavigationRow className='box-baseline'>
        <NavigationGroup>
            <TooltipLink className='dropdown-item' to={to} onClick={() => close()}>
                <span className='box-aligned'>
                    <HomeIcon size={fs_regular} className='mr-1'/><span>{title}</span>
                </span>
            </TooltipLink>
        </NavigationGroup>
        <NavigationGroup>
            <RoundedButton onClick={close}>
                <CloseIcon size={fs_regular} className='navigation-item-color'/>
            </RoundedButton>
        </NavigationGroup>
    </NavigationRow>
};

const PageLink = ({children, principalUsername, page, close, ...props}) => {
    return <Link className='dropdown-item' to={`/${principalUsername}/page/${page}`}
                 onClick={close}  {...props}>
        {children}
    </Link>
};

const SlideoutNavigation = () => {
    const {close} = useContext(SlideoutContext);
    const {isAuthorized, isSuperUser, principal, principalUsername} = useAuthorization();
    const {homePage, eventsCalendarPage, spacesCalendarPage, festivalsCalendarPage} = useSiteConfiguration();
    const isRegistration = useSelector(state => getIsRegistration(state));
    const {Lang} = useConfiguration();
    const dispatch = useDispatch();


    const handleLogout = (event) => {
        event.preventDefault();
        dispatch(logoutRequest());
    }

    const renderCurrentUser = () => {
        if (isAuthorized) {
            const avatar = getUserAvatar(principal);

            return <div className='navigation-user-container'>
                {avatar && <img className='navigation-user-avatar-small' src={avatar}/>}
                {principal.firstname} {principal.lastname}
            </div>
        }
        return null;
    }

    const handleCloseSlideOut = (event) => {
        close();
    }

    return <nav className="navbar navbar-expand-sm">
        <div className="slideout-navigation slideout-navigation-menu py-1">
            <HomeLink to={homePage}
                      close={handleCloseSlideOut}
                      title={Lang.nav.header.home}/>

            <TooltipLink className='box-baseline' onClick={handleCloseSlideOut} to={eventsCalendarPage}>
            <span><EventIcon size={fs_regular} className='mr-1'/>
                {Lang.nav.header.events.mobile}
            </span>
            </TooltipLink>

            <TooltipLink className='box-baseline' onClick={handleCloseSlideOut} to={festivalsCalendarPage}>
            <span><CelebrationIcon size={fs_regular} className='mr-1'/>
                {Lang.nav.header.festivals.mobile}
            </span>
            </TooltipLink>

            <TooltipLink className='box-baseline' onClick={handleCloseSlideOut} to={spacesCalendarPage}>
            <span><AppsIcon size={fs_regular} className='mr-1'/>
                {Lang.nav.header.spaces.mobile}
            </span>
            </TooltipLink>

            <div className="dropdown-divider"/>
            {isSuperUser && <TooltipLink className='box-baseline mt-1' to="/manage/site" onClick={handleCloseSlideOut}>
                <span><SettingsIcon size={fs_regular} className='mr-1'/>{Lang.nav.submenu.manageSite}</span>
            </TooltipLink>}

            {isAuthorized &&
                <TooltipLink className='box-baseline mt-1' to="/update/account" onClick={handleCloseSlideOut}>
                    <span><PersonEditIcon size={fs_regular} className='font-1-bigger mr-1'/>{Lang.nav.submenu.yourAccount}</span>
                </TooltipLink>}

            {(isSuperUser || isRegistration) && <Fragment>
                <div className="dropdown-divider"/>
                <TooltipLink className='box-baseline' to="/create/account" onClick={handleCloseSlideOut}>
                    <span><PersonAddIcon size={fs_regular} className='mr-1'/>{Lang.nav.submenu.createAccount}</span>
                </TooltipLink></Fragment>}

            {isAuthorized && <div>
                <div className="dropdown-divider"/>

                <TooltipLink className='box-baseline mt-1' onClick={handleCloseSlideOut}
                             to={`/${principalUsername}/friends`}>
                    <span><ChatIcon size={fs_regular} className='mr-1'/>{Lang.generic.nav.friends.chat}</span>
                </TooltipLink>

                <TooltipLink className='box-baseline mt-1' onClick={handleCloseSlideOut}
                             to={`/${principalUsername}/calendar`}>
                    <span><EventIcon size={fs_regular} className='mr-1'/>{Lang.nav.sidebar.calendar.mine}</span>
                </TooltipLink>

                <TooltipLink className='box-baseline mt-1' onClick={handleCloseSlideOut} to='/'>
                    <span><TodayIcon size={fs_regular} className='mr-1'/>{Lang.nav.sidebar.calendar.mineToday}</span>
                </TooltipLink>
            </div>}

            {isAuthorized && <div className="dropdown-divider"/>}

            {isAuthorized && <TooltipLink className='box-baseline mt-2' to={`/${principalUsername}/home`}>
                <span>{renderCurrentUser()}</span>
            </TooltipLink>}

            {!isAuthorized && <TooltipLink className='box-baseline mt-1' to="/login" onClick={handleCloseSlideOut}>
                <span><AccountCircleIcon size={fs_regular} className='mr-1'/>{Lang.nav.submenu.login}</span>
            </TooltipLink>}

            <TooltipLink className='box-baseline mt-1' to="/" onClick={handleLogout}>
                <span><PowerSettingsIcon size={fs_regular} className='mr-1'/>{Lang.nav.submenu.logout}</span>
            </TooltipLink>

            <div className="dropdown-divider"/>

            <PageLink className='box-baseline' principalUsername={principalUsername} close={handleCloseSlideOut} page={getImprintPageUrl()}>
                {Lang.nav.header.imprint}
            </PageLink>
            <PageLink className='box-baseline' principalUsername={principalUsername} close={handleCloseSlideOut}
                      page={getPrivacyPolicyPageUrl()}>
                {Lang.nav.header.privacy}
            </PageLink>
            <PageLink className='box-baseline' principalUsername={principalUsername} close={handleCloseSlideOut}
                      page={getTermsOfUsePageUrl()}>
                {Lang.nav.header.terms}
            </PageLink>

            <div className="dropdown-divider"/>
            <ul className="navbar-nav">
                <div className="btn-group" role="group">
                    <button type="button" className="dropdown-toggle btn btn-sm"
                            data-bs-toggle="dropdown" aria-haspopup="true" aria-expanded="false">
                        {localeFlags[Lang.locale]}
                    </button>
                    <NavigationLanguageDropdown/>
                </div>
            </ul>

        </div>


    </nav>
}

export default React.memo(SlideoutNavigation);